import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ArrowRight, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface MeetingGuideProps {
  step: number;
  onComplete: () => void;
  onSkip: () => void;
  onNext: () => void;
}

export default function MeetingGuide({ step, onComplete, onSkip, onNext }: MeetingGuideProps) {
  const { t } = useTranslation('common');
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const steps = [
    {
      title: t('meetingGuide.step1_title'),
      description: t('meetingGuide.step1_description'),
      target: 'add-topic-button',
      position: 'right' as const
    },
    {
      title: t('meetingGuide.step2_title'),
      description: t('meetingGuide.step2_description'),
      target: 'add-item-button',
      position: 'left' as const
    },
    {
      title: t('meetingGuide.step3_title'),
      description: t('meetingGuide.step3_description'),
      target: 'item-form',
      position: 'top' as const
    }
  ];

  useEffect(() => {
    if (step >= steps.length) {
      onComplete();
      return;
    }

    const currentStep = steps[step];
    const element = document.querySelector(`[data-guide="${currentStep.target}"]`) as HTMLElement;

    if (element) {
      setTargetElement(element);
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });

      const updatePosition = () => {
        const rect = element.getBoundingClientRect();

        let top = rect.top;
        let left = rect.left;

        switch (currentStep.position) {
          case 'right':
            top = rect.top + rect.height / 2;
            left = rect.right + 20;
            break;
          case 'left':
            top = rect.top + rect.height / 2;
            left = rect.left - 320;
            break;
          case 'top':
            top = rect.top - 180;
            left = rect.left + rect.width / 2 - 150;
            break;
        }

        setPosition({ top, left });
      };

      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition);

      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition);
      };
    }
  }, [step, onComplete]);

  if (step >= steps.length) return null;
  const currentStep = steps[step];

  return (
    <>
      {/* Overlay with spotlight */}
      {targetElement && (
        <>
          <div className="fixed inset-0 z-[9998]" style={{ pointerEvents: 'none' }}>
            <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0 }}>
              <defs>
                <mask id="guide-mask">
                  <rect x="0" y="0" width="100%" height="100%" fill="white" />
                  <rect
                    x={targetElement.getBoundingClientRect().left - 8}
                    y={targetElement.getBoundingClientRect().top - 8}
                    width={targetElement.getBoundingClientRect().width + 16}
                    height={targetElement.getBoundingClientRect().height + 16}
                    rx="12"
                    fill="black"
                  />
                </mask>
              </defs>
              <rect x="0" y="0" width="100%" height="100%" fill="rgba(0, 0, 0, 0.5)" mask="url(#guide-mask)" />
            </svg>
          </div>
          {/* Highlight border */}
          <div
            className="fixed z-[9999] pointer-events-none rounded-xl border-[3px] border-primary animate-pulse"
            style={{
              top: targetElement.getBoundingClientRect().top - 8,
              left: targetElement.getBoundingClientRect().left - 8,
              width: targetElement.getBoundingClientRect().width + 16,
              height: targetElement.getBoundingClientRect().height + 16,
              boxShadow: '0 0 0 4px hsl(var(--primary) / 0.2), 0 0 30px hsl(var(--primary) / 0.4)',
            }}
          />
        </>
      )}

      <Card
        className={cn(
          "fixed z-[10000] w-80 shadow-2xl border-0",
          "animate-in fade-in-0 zoom-in-95 duration-300"
        )}
        style={{
          top: position.top,
          left: position.left
        }}
      >
        <CardHeader className="pb-2 relative">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onSkip}
            className="absolute top-2 right-2 h-8 w-8 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">{t('meetingGuide.skip_tutorial')}</span>
          </Button>

          <Badge className="w-fit mb-2 bg-primary/10 text-primary border-0 hover:bg-primary/10">
            {t('meetingGuide.step_of', { current: step + 1, total: steps.length })}
          </Badge>

          <CardTitle className="text-lg">{currentStep.title}</CardTitle>
          <CardDescription className="leading-relaxed">
            {currentStep.description}
          </CardDescription>

          {step < steps.length - 1 && (
            <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
              <ArrowRight size={14} className="text-primary" />
              <span>{t('meetingGuide.click_highlighted_area')}</span>
            </div>
          )}
        </CardHeader>

        <CardContent className="pt-2">
          {/* Arrow indicator */}
          <div
            className="absolute"
            style={{
              ...(currentStep.position === 'right' && {
                left: -30,
                top: '50%',
                transform: 'translateY(-50%)'
              }),
              ...(currentStep.position === 'left' && {
                right: -30,
                top: '50%',
                transform: 'translateY(-50%) rotate(180deg)'
              }),
              ...(currentStep.position === 'top' && {
                bottom: -30,
                left: '50%',
                transform: 'translateX(-50%) rotate(90deg)'
              })
            }}
          >
            <svg width="30" height="30" viewBox="0 0 30 30" fill="none" className="animate-bounce-x">
              <path
                d="M15 5L25 15L15 25L15 18C8 18 5 22 3 27C3 20 5 13 15 12L15 5Z"
                className="fill-primary"
              />
            </svg>
          </div>

          <Button
            onClick={onNext}
            className="w-full"
          >
            {step < steps.length - 1 ? (
              <>
                {t('next')}
                <ArrowRight className="h-4 w-4" />
              </>
            ) : (
              <>
                {t('meetingGuide.complete_tutorial')}
                <Check className="h-4 w-4" />
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <style>{`
        @keyframes bounceX {
          0%, 100% {
            transform: translateX(0);
          }
          50% {
            transform: translateX(-5px);
          }
        }
        .animate-bounce-x {
          animation: bounceX 1.5s ease-in-out infinite;
        }
      `}</style>
    </>
  );
}
